/**
 * FP3-035E — Shared Session Documentation Rows.
 *
 * Extracts the per-student attendance + multi-goal evidence markup
 * that was previously duplicated (~80%) between:
 *
 *   - LogSessionDialog.jsx       (scheduled-session documentation)
 *   - DocumentQuickSessionDialog.jsx (ad-hoc quick-session documentation)
 *
 * Both flows now render this component for every selected student;
 * the parent dialog still owns the modal chrome, date/time context,
 * service minutes, and submit handler. State shape is identical
 * across both flows:
 *
 *   perStudent[sid] = {
 *     attendance: "present" | "absent" | "unavailable",
 *     student_note: "",
 *     goal_rows: [
 *       { goal_id, entry_type, numerator, denominator, assessment_label }
 *     ],
 *   }
 *
 * The component is intentionally presentational — all state lives
 * in the parent. The parent passes setters so each dialog can keep
 * its existing save semantics (Log Session: launchSessions →
 * logSession; Quick Session: createSession → logSession).
 *
 * Testids are namespaced by the `testIdPrefix` prop so existing
 * Playwright selectors in iter 64 / 68 / 69 keep working. Pass
 * `"log"` for Log Session, `"doc-quick"` for Document Session.
 */
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightning } from "@phosphor-icons/react";


export const ATTENDANCE_OPTIONS = [
  // FP3-114 — Bolder active states so the segmented control's
  // current selection is obvious at a glance. Previous "unavailable"
  // colour was almost indistinguishable from the inactive white
  // buttons, which made providers think their click didn't register.
  { value: "present", label: "Present", color: "bg-[#1E3F33] text-white border-[#1E3F33]" },
  { value: "absent", label: "Absent", color: "bg-[#B85C5C] text-white border-[#B85C5C]" },
  { value: "unavailable", label: "Unavailable", color: "bg-[#9C6B2E] text-white border-[#9C6B2E]" },
];


export const ENTRY_TYPES = [
  { value: "narrative_observation", label: "Narrative — notes only, won't plot" },
  { value: "percent_trials", label: "Percent / Trials — chartable data point" },
  { value: "informal_assessment", label: "Informal Assessment / Probe — chartable when scored" },
  { value: "other", label: "Other — won't plot" },
];


/**
 * Default per-student row used by both dialogs when a new student
 * enters the documentation surface.
 */
export const makeInitialStudentRow = () => ({
  attendance: "present",
  student_note: "",
  goal_rows: [],
});


/**
 * Pure helpers shared by both dialogs to keep the state-shape rules
 * (default fields, narrative_observation default, etc.) in one
 * place. Used by both Log Session and Document Session toggles.
 */
export function toggleGoalInRow(row, goalId) {
  const cur = row || makeInitialStudentRow();
  const existing = cur.goal_rows || [];
  const idx = existing.findIndex((r) => r.goal_id === goalId);
  let nextRows;
  if (idx >= 0) {
    nextRows = existing.filter((r) => r.goal_id !== goalId);
  } else {
    nextRows = [
      ...existing,
      {
        goal_id: goalId,
        entry_type: "narrative_observation",
        numerator: "",
        denominator: "",
        assessment_label: "",
      },
    ];
  }
  return { ...cur, goal_rows: nextRows };
}


export function updateGoalRowField(row, goalId, field, value) {
  if (!row) return row;
  return {
    ...row,
    goal_rows: (row.goal_rows || []).map((r) =>
      r.goal_id === goalId ? { ...r, [field]: value } : r,
    ),
  };
}


/**
 * Shared header strip — "Attendance & Evidence" label + optional
 * Mark all present link. Pulled out so both dialogs can include it
 * (or omit) without re-implementing the markup.
 */
function EvidenceHeader({ testIdPrefix, isGroup, onMarkAllPresent }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-xs font-medium text-[#1F2937]">
        Attendance & Evidence
      </Label>
      {isGroup && (
        <button
          type="button"
          data-testid={`${testIdPrefix}-mark-all-present`}
          onClick={onMarkAllPresent}
          className="text-[11px] font-medium text-[#1E3F33] underline-offset-2 hover:underline"
        >
          Mark all present
        </button>
      )}
    </div>
  );
}


/**
 * A single student card. Identical markup in both prior dialogs;
 * the only difference was testid prefix and the surrounding wrapper.
 */
function StudentDocumentationCard({
  student,
  row,
  goals,
  testIdPrefix,
  setAttendance,
  setStudentNote,
  toggleGoal,
  setGoalRowField,
}) {
  const isPresent = row.attendance === "present";
  const selectedGoalRows = row.goal_rows || [];
  const selectedGoalIds = new Set(selectedGoalRows.map((r) => r.goal_id));

  return (
    <div
      key={student.id}
      data-testid={`${testIdPrefix}-row-${student.id}`}
      className="border border-[#E5E3DB] rounded-xl p-3 bg-white space-y-2"
    >
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-medium text-[#1F2937]">
          {student.first_name} {student.last_name}
        </p>
        <div className="flex gap-1">
          {ATTENDANCE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              data-testid={`${testIdPrefix}-attendance-${student.id}-${opt.value}`}
              onClick={() => setAttendance(student.id, opt.value)}
              className={`px-2 py-1 rounded-md text-[10px] font-medium border transition ${
                row.attendance === opt.value
                  ? opt.color
                  : "bg-white text-[#6B7280] border-[#E5E3DB] hover:border-[#9B9890]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isPresent && (
        <div className="space-y-2">
          <p className="text-[10px] text-[#6B7280] -mt-0.5 leading-snug">
            Pick the goal(s) addressed below. Each selected goal gets the
            same shared note plus optional chartable data per goal.
          </p>

          {/* Multi-goal checklist + per-goal evidence cards. */}
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              Goal(s) addressed (optional)
              <span className="text-[#9B9890] normal-case tracking-normal ml-1">
                · {selectedGoalRows.length} selected
              </span>
            </Label>
            {goals.length === 0 ? (
              <p
                data-testid={`${testIdPrefix}-no-goals-${student.id}`}
                className="text-[11px] text-[#9B9890] mt-1 italic"
              >
                No active goals for this student yet.
              </p>
            ) : (
              <div
                data-testid={`${testIdPrefix}-goal-list-${student.id}`}
                className="mt-1 space-y-1 max-h-44 overflow-y-auto pr-1"
              >
                {goals.map((g) => {
                  const checked = selectedGoalIds.has(g.id);
                  const goalRow = selectedGoalRows.find((r) => r.goal_id === g.id);
                  const isPercent = goalRow?.entry_type === "percent_trials";
                  const isInformal = goalRow?.entry_type === "informal_assessment";
                  return (
                    <div
                      key={g.id}
                      data-testid={`${testIdPrefix}-goal-item-${student.id}-${g.id}`}
                      className={`rounded-md border transition ${
                        checked
                          ? "border-[#1E3F33]/30 bg-[#F1F7F2]"
                          : "border-[#E5E3DB] bg-white"
                      }`}
                    >
                      <label className="flex items-start gap-2 px-2 py-1.5 cursor-pointer">
                        <Checkbox
                          data-testid={`${testIdPrefix}-goal-toggle-${student.id}-${g.id}`}
                          checked={checked}
                          onCheckedChange={() => toggleGoal(student.id, g.id)}
                          className="mt-0.5"
                        />
                        <span className="flex-1 min-w-0">
                          <span className="block text-[11px] font-medium text-[#1F2937]">
                            {g.category || "Goal"}
                          </span>
                          <span className="block text-[10.5px] text-[#6B7280] leading-snug">
                            {g.goal_text || ""}
                          </span>
                        </span>
                      </label>
                      {checked && goalRow && (
                        <div
                          data-testid={`${testIdPrefix}-goal-evidence-${student.id}-${g.id}`}
                          className="border-t border-[#E5E3DB]/70 px-2 py-2 space-y-2 bg-white/60 rounded-b-md"
                        >
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-[9px] uppercase tracking-wide text-[#6B7280]">
                                Evidence type
                              </Label>
                              <Select
                                value={goalRow.entry_type || "narrative_observation"}
                                onValueChange={(v) =>
                                  setGoalRowField(student.id, g.id, "entry_type", v)
                                }
                              >
                                <SelectTrigger
                                  data-testid={`${testIdPrefix}-entry-type-${student.id}-${g.id}`}
                                  className="text-xs h-8"
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ENTRY_TYPES.map((t) => (
                                    <SelectItem key={t.value} value={t.value} className="text-xs">
                                      {t.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {isInformal && (
                              <div>
                                <Label className="text-[9px] uppercase tracking-wide text-[#6B7280]">
                                  Probe label
                                </Label>
                                <Input
                                  data-testid={`${testIdPrefix}-assessment-${student.id}-${g.id}`}
                                  value={goalRow.assessment_label}
                                  onChange={(e) =>
                                    setGoalRowField(
                                      student.id,
                                      g.id,
                                      "assessment_label",
                                      e.target.value,
                                    )
                                  }
                                  placeholder="e.g. Writing probe"
                                  className="text-xs h-8"
                                />
                              </div>
                            )}
                          </div>
                          {(isPercent || isInformal) && (
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[9px] uppercase tracking-wide text-[#6B7280]">
                                  Correct
                                </Label>
                                <Input
                                  data-testid={`${testIdPrefix}-numerator-${student.id}-${g.id}`}
                                  type="number"
                                  min="0"
                                  value={goalRow.numerator}
                                  onChange={(e) =>
                                    setGoalRowField(
                                      student.id,
                                      g.id,
                                      "numerator",
                                      e.target.value,
                                    )
                                  }
                                  className="text-xs h-8"
                                />
                              </div>
                              <div>
                                <Label className="text-[9px] uppercase tracking-wide text-[#6B7280]">
                                  Total
                                </Label>
                                <Input
                                  data-testid={`${testIdPrefix}-denominator-${student.id}-${g.id}`}
                                  type="number"
                                  min="0"
                                  value={goalRow.denominator}
                                  onChange={(e) =>
                                    setGoalRowField(
                                      student.id,
                                      g.id,
                                      "denominator",
                                      e.target.value,
                                    )
                                  }
                                  className="text-xs h-8"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FP3-114 — Per-student note always visible regardless of
          attendance. Persists on the session's student_notes map so
          Session History surfaces it under exactly one participant.
          Not gated on goal selection or chartable data. */}
      <div>
        <Label className="text-[11px] font-medium text-[#1F2937] mb-1 block">
          Session note for this student
        </Label>
        <Textarea
          data-testid={`${testIdPrefix}-note-${student.id}`}
          value={row.student_note || ""}
          onChange={(e) => setStudentNote(student.id, e.target.value)}
          placeholder={
            isPresent
              ? "Add observations, prompts, or context specific to this student"
              : "Optional context — e.g. reason for absence"
          }
          className="min-h-[56px] text-xs"
        />
        <p className="text-[10px] text-[#6B7280] mt-1">
          This note will appear in this student's Session History.
        </p>
      </div>

      {!isPresent && (
        <p className="text-[10px] text-[#9C6B2E] flex items-center gap-1">
          <Lightning size={11} weight="duotone" />
          0 delivered minutes — fidelity will reflect this absence.
        </p>
      )}
    </div>
  );
}


/**
 * Top-level container — renders the section header + the
 * 2-column grid of `StudentDocumentationCard`s.
 *
 * Props:
 *   students            — list of student records {id, first_name, last_name, …}
 *   perStudent          — keyed map of per-student row state
 *   goalsByStudent      — keyed map of active goals per student
 *   testIdPrefix        — "log" or "doc-quick"
 *   setAttendance       — (sid, value) => void
 *   setStudentNote      — (sid, value) => void
 *   toggleGoal          — (sid, goalId) => void
 *   setGoalRowField     — (sid, goalId, field, value) => void
 *   onMarkAllPresent    — () => void (called from the optional link)
 *
 * Renders nothing when `students` is empty (callers that have a
 * "Select student" prelude rely on this to keep the grid hidden).
 */
export default function SessionDocumentationRows({
  students,
  perStudent,
  goalsByStudent,
  testIdPrefix,
  setAttendance,
  setStudentNote,
  toggleGoal,
  setGoalRowField,
  onMarkAllPresent,
}) {
  if (!students || students.length === 0) return null;
  const isGroup = students.length > 1;
  return (
    <div className="space-y-2">
      <EvidenceHeader
        testIdPrefix={testIdPrefix}
        isGroup={isGroup}
        onMarkAllPresent={onMarkAllPresent}
      />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {students.map((s) => (
          <StudentDocumentationCard
            key={s.id}
            student={s}
            row={perStudent[s.id] || makeInitialStudentRow()}
            goals={goalsByStudent[s.id] || []}
            testIdPrefix={testIdPrefix}
            setAttendance={setAttendance}
            setStudentNote={setStudentNote}
            toggleGoal={toggleGoal}
            setGoalRowField={setGoalRowField}
          />
        ))}
      </div>
    </div>
  );
}
